import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent,
  CircularProgress, Alert, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Link,
} from '@mui/material';
import { ArrowBackOutlined, WarningAmberOutlined } from '@mui/icons-material';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from 'recharts';
import type { RootState } from '../../store/store';
import {
  useGetLanguageAnalysisQuery,
  useGetLanguageAnalysisInsightsQuery,
} from '../../store/api/adminApi';

const ADMIN_EMAIL = 'mmohammad@leangains.ai';

const SEVERITY_COLORS: Record<string, string> = {
  critical: '#dc2626',
  high: '#ea580c',
  medium: '#d97706',
  low: '#65a30d',
  info: '#6b7280',
};

const TOOL_COLORS = [
  '#7c3aed', '#0891b2', '#dc2626', '#059669', '#d97706',
  '#2563eb', '#db2777', '#65a30d', '#9333ea',
];

function SectionCard({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
      <CardContent>
        <Typography variant="h6" fontWeight={700} sx={{ mb: subtitle ? 0.25 : 1.5 }}>{title}</Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{subtitle}</Typography>
        )}
        {children}
      </CardContent>
    </Card>
  );
}

function IssuesByLanguageChart({ data }: { data: Record<string, Record<string, number>> }) {
  const severities = Array.from(
    new Set(Object.values(data).flatMap((s) => Object.keys(s))),
  );
  const chartData = Object.entries(data).map(([language, bySeverity]) => ({
    language,
    ...bySeverity,
  }));

  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart data={chartData} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="language" />
        <YAxis />
        <Tooltip />
        <Legend />
        {severities.map((sev) => (
          <Bar key={sev} dataKey={sev} stackId="severity" fill={SEVERITY_COLORS[sev] ?? '#94a3b8'} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

function ToolActivityChart({ data }: { data: Record<string, Record<string, number>> }) {
  const allWeeks = Array.from(
    new Set(Object.values(data).flatMap((w) => Object.keys(w))),
  ).sort();
  const chartData = allWeeks.map((week) => {
    const row: Record<string, string | number> = { week };
    for (const tool of Object.keys(data)) {
      row[tool] = data[tool][week] ?? 0;
    }
    return row;
  });
  const tools = Object.keys(data);

  return (
    <ResponsiveContainer width="100%" height={320}>
      <LineChart data={chartData} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="week" />
        <YAxis />
        <Tooltip />
        <Legend />
        {tools.map((tool, i) => (
          <Line
            key={tool}
            type="monotone"
            dataKey={tool}
            stroke={TOOL_COLORS[i % TOOL_COLORS.length]}
            strokeWidth={2}
            dot={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

function DensityChart({ data }: { data: Record<string, number> }) {
  const chartData = Object.entries(data)
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([language, density]) => ({ language, density }));

  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart data={chartData} layout="vertical" margin={{ top: 8, right: 24, left: 24, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" />
        <YAxis type="category" dataKey="language" width={90} />
        <Tooltip />
        <Bar dataKey="density" fill="#0891b2" name="issues / 1000 LOC" />
      </BarChart>
    </ResponsiveContainer>
  );
}

function CategoryMixTable({ data }: { data: Record<string, Record<string, number>> }) {
  const categories = Array.from(
    new Set(Object.values(data).flatMap((c) => Object.keys(c))),
  ).sort();

  return (
    <TableContainer component={Paper} elevation={0} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell><strong>Language</strong></TableCell>
            {categories.map((cat) => (
              <TableCell key={cat} align="right"><strong>{cat}</strong></TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(data).map(([language, cats]) => (
            <TableRow key={language}>
              <TableCell>{language}</TableCell>
              {categories.map((cat) => (
                <TableCell key={cat} align="right">
                  {(cats[cat] ?? 0).toLocaleString()}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function CoverageGapsList({ gaps }: { gaps: { language: string; category: string }[] }) {
  if (gaps.length === 0) {
    return <Typography variant="body2" color="text.secondary">No coverage gaps detected.</Typography>;
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {gaps.map((gap) => (
        <Chip
          key={`${gap.language}-${gap.category}`}
          icon={<WarningAmberOutlined />}
          label={`${gap.language} · ${gap.category}`}
          size="small"
          color="warning"
          variant="outlined"
        />
      ))}
    </Box>
  );
}

function InsightsPanel() {
  const { data, isLoading, error } = useGetLanguageAnalysisInsightsQuery();

  return (
    <SectionCard
      title="AI Insights"
      subtitle="A single model call over the aggregates below — surfaces patterns worth a second look, not a restatement of every number."
    >
      {isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">Generating insights…</Typography>
        </Box>
      )}
      {error && <Alert severity="error">Failed to generate insights.</Alert>}
      {data && (
        <Box sx={{ whiteSpace: 'pre-line', fontSize: 14, lineHeight: 1.8 }}>
          {data.insights}
        </Box>
      )}
    </SectionCard>
  );
}

const AdminLanguageAnalysisPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const isAdmin = (user?.email ?? '').toLowerCase() === ADMIN_EMAIL.toLowerCase();
  const { data, isLoading, error } = useGetLanguageAnalysisQuery(undefined, { skip: !isAdmin });

  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 400, gap: 2 }}>
        <CircularProgress />
        <Typography color="text.secondary">Loading analysis…</Typography>
      </Box>
    );
  }

  if (error || !data) {
    return <Alert severity="error">Failed to load language analysis.</Alert>;
  }

  return (
    <Box sx={{ p: 4, maxWidth: 1100, mx: 'auto' }}>
      <Box sx={{ mb: 4 }}>
        <Link component={RouterLink} to="/admin" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 1.5, fontSize: 14 }}>
          <ArrowBackOutlined fontSize="small" /> Back to overview
        </Link>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <Typography variant="h4" fontWeight={800}>Language & Tooling Coverage</Typography>
          <Chip label="Internal" size="small" color="warning" sx={{ fontWeight: 700 }} />
        </Box>
        <Typography variant="body2" color="text.secondary">
          Where debt is concentrated by language, and whether every detection tool is still
          actually producing findings.
        </Typography>
      </Box>

      <InsightsPanel />

      <SectionCard title="Issues by language" subtitle="Severity breakdown across all scanned repositories.">
        <IssuesByLanguageChart data={data.issues_by_language} />
      </SectionCard>

      <SectionCard
        title="Tool activity, last 10 weeks"
        subtitle="A tool's line trending toward zero while others stay active is the signal to watch — that's exactly the shape of bug that silently broke ESLint for TypeScript repos before a customer noticed."
      >
        <ToolActivityChart data={data.tool_activity_trend} />
      </SectionCard>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <SectionCard title="Issue density" subtitle="Issues per 1,000 lines of code, by language.">
            <DensityChart data={data.density_per_1000_loc} />
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <SectionCard title="Coverage gaps" subtitle="Language / category pairs with zero issues ever recorded.">
            <CoverageGapsList gaps={data.coverage_gaps} />
          </SectionCard>
        </Grid>
      </Grid>

      <SectionCard title="Category mix by language">
        <CategoryMixTable data={data.category_mix_by_language} />
      </SectionCard>
    </Box>
  );
};

export default AdminLanguageAnalysisPage;
